import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ShoppingCart,
  IndianRupee,
  Layers,
  Boxes,
  Tag,
  Barcode,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  CreditCard,
  Building2,
  FileText,
  Plus,
  Minus,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StandardImage, PageHeader } from "@/components/shared";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/services/httpClient";
import { channelOrdersService, type ChannelCatalogueItem } from "@/services/channelOrdersService";
import { useChannelDesk } from "@/lib/channel-desk";

const paymentMethods = [
  { value: "0", label: "P2P (Peer-to-Peer)", description: "Direct settlement with the supplier" },
  { value: "1", label: "Cash", description: "Cash on delivery / receipt" },
  { value: "2", label: "Online", description: "Online UPI / Bank transfer" },
];

export default function ChannelPlaceOrderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const desk = useChannelDesk();
  const location = useLocation();

  const productId = Number(id);
  const catalogueUrl = desk.base === "/store/supply" ? "/store/supply" : `${desk.base}/catalogue`;
  const ordersUrl = desk.base === "/store/supply" ? "/store/supply/orders" : `${desk.base}/orders`;

  const userRoleType = useMemo<"store" | "wholesaler" | "distributor">(() => {
    if (location.pathname.startsWith("/store") || desk.base === "/store/supply" || desk.role === "Retailer") {
      return "store";
    }
    if (location.pathname.startsWith("/wholesaler") || desk.base === "/wholesaler" || desk.role === "Wholesaler") {
      return "wholesaler";
    }
    return "distributor";
  }, [location.pathname, desk.base, desk.role]);

  // Fetch product from catalogue
  const productQuery = useQuery({
    queryKey: ["channel", "catalogue", "item", userRoleType, productId],
    queryFn: async () => {
      // Fetch specific item or fallback to catalogue search
      try {
        return await channelOrdersService.getCatalogueItem(productId);
      } catch {
        const all = await channelOrdersService.catalogue();
        const found = all.find((p) => p.productId === productId);
        if (!found) throw new Error("Product not found in catalogue");
        return found;
      }
    },
    enabled: Boolean(productId),
  });

  const product = productQuery.data;
  const availableStock = product?.availableQuantity ?? 0;
  const isOutOfStock = availableStock <= 0;

  // Form states
  const [quantity, setQuantity] = useState<number>(1);
  const [paymentMethod, setPaymentMethod] = useState<string>("0");
  const [paymentReference, setPaymentReference] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [qtyError, setQtyError] = useState<string | null>(null);

  // Auto-adjust initial quantity if stock is lower or 0
  useEffect(() => {
    if (availableStock > 0 && quantity > availableStock) {
      setQuantity(availableStock);
    } else if (availableStock <= 0) {
      setQuantity(0);
    } else if (quantity < 1 && availableStock > 0) {
      setQuantity(1);
    }
  }, [availableStock]);

  // Quantity handlers ensuring strictly never > availableStock and never < 1
  const handleQuantityChange = (val: number) => {
    if (isOutOfStock) {
      setQuantity(0);
      return;
    }

    if (val > availableStock) {
      setQuantity(availableStock);
      setQtyError(`Maximum available stock with ${desk.availableWith} is ${availableStock} units.`);
    } else if (val < 1) {
      setQuantity(1);
      setQtyError("Quantity must be at least 1 unit.");
    } else {
      setQuantity(val);
      setQtyError(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === "") {
      setQuantity(1);
      setQtyError(null);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) return;
    handleQuantityChange(parsed);
  };


  const getRolePurchasePrice = (p: ChannelCatalogueItem | undefined | null) => {
    if (!p) return 0;
    if (userRoleType === "store") {
      return p.retailPrice && p.retailPrice > 0 ? p.retailPrice : (p.price > 0 ? p.price : (p.customerPrice || p.mrp));
    }
    if (userRoleType === "wholesaler") {
      return p.wholesalePrice && p.wholesalePrice > 0 ? p.wholesalePrice : (p.price > 0 ? p.price : (p.customerPrice || p.mrp));
    }
    return p.distributorPrice && p.distributorPrice > 0 ? p.distributorPrice : (p.price > 0 ? p.price : (p.customerPrice || p.mrp));
  };

  const rolePriceLabel = useMemo(() => {
    if (userRoleType === "store") return "Store Buy Price (RP)";
    if (userRoleType === "wholesaler") return "Wholesaler Buy Price (WP)";
    return "Distributor Buy Price (DP)";
  }, [userRoleType]);

  // Live dynamic totals based on role purchase price
  const unitPrice = getRolePurchasePrice(product);
  const totalAmount = unitPrice * quantity;
  const totalBv = (product?.businessVolume ?? 0) * quantity;
  const onwardCommissionPercent = product?.onwardCommissionPercent ?? 0;
  const estimatedCommission = Math.round((totalAmount * onwardCommissionPercent) / 100);

  // Place Order Mutation
  const placeOrderMutation = useMutation({
    mutationFn: () =>
      channelOrdersService.create({
        productId,
        quantity,
        paymentMethod: Number(paymentMethod),
        paymentReference: paymentReference.trim() || undefined,
        note: note.trim() || undefined,
      }),
    onSuccess: (order) => {
      toast({
        title: "Order Placed Successfully",
        description: `Order #${order.id} for ${quantity} unit(s) of "${product?.name}" was placed with ${desk.availableWith}.`,
      });
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["channel", "orders"] });
      queryClient.invalidateQueries({ queryKey: ["channel", "catalogue"] });
      queryClient.invalidateQueries({ queryKey: ["channel", "stock"] });
      // Navigate to orders list
      navigate(ordersUrl);
    },
    onError: (err) => {
      toast({
        title: "Could Not Place Order",
        description: getApiErrorMessage(err),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isOutOfStock) {
      toast({
        title: "Out of Stock",
        description: "This product is currently out of stock with your supplier.",
        variant: "destructive",
      });
      return;
    }
    if (quantity <= 0 || quantity > availableStock) {
      setQtyError(`Please enter a quantity between 1 and ${availableStock}.`);
      return;
    }
    placeOrderMutation.mutate();
  };

  const formatInr = (amt: number) => `₹${Number(amt || 0).toLocaleString("en-IN")}`;

  if (productQuery.isLoading) {
    return (
      <div className="space-y-6 max-w-5xl mx-auto py-2">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-28" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-[400px] w-full rounded-2xl" />
          <Skeleton className="h-[400px] w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (productQuery.isError || !product) {
    return (
      <div className="max-w-md mx-auto my-12 text-center space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
        <h2 className="text-xl font-bold font-heading">Product Not Found</h2>
        <p className="text-sm text-muted-foreground">
          {productQuery.error ? getApiErrorMessage(productQuery.error) : "This product could not be loaded from the catalogue."}
        </p>
        <Button asChild variant="outline">
          <Link to={catalogueUrl} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Catalogue
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">
      {/* Navigation Breadcrumb & Back Button */}
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
          <Link to={catalogueUrl}>
            <ArrowLeft className="h-4 w-4" />
            Back to {desk.catalogueTitle}
          </Link>
        </Button>
        <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
          Supplied by {desk.availableWith}
        </Badge>
      </div>

      <PageHeader
        title="Place Stock Order"
        description={`Order stock from ${desk.availableWith}. Stock and commission will move once approved.`}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Full Product Details (5 Cols) */}
        <div className="lg:col-span-5 space-y-5">
          <Card className="border border-border/80 shadow-sm overflow-hidden">
            <div className="p-4 bg-muted/20 border-b flex items-center justify-center">
              <StandardImage
                src={product.imageUrl}
                alt={product.name}
                standard="product"
                className="w-full max-w-[280px] h-[280px] object-contain rounded-xl bg-white border shadow-inner"
              />
            </div>
            <CardContent className="p-5 space-y-4">
              <div>
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  {product.categoryName && (
                    <Badge variant="secondary" className="gap-1 text-xs">
                      <Tag className="h-3 w-3" />
                      {product.categoryName}
                    </Badge>
                  )}
                  {product.sku && (
                    <Badge variant="outline" className="font-mono text-xs">
                      SKU: {product.sku}
                    </Badge>
                  )}
                  {product.barcode && (
                    <Badge variant="outline" className="font-mono text-xs gap-1">
                      <Barcode className="h-3 w-3" />
                      {product.barcode}
                    </Badge>
                  )}
                </div>
                <h2 className="text-xl font-bold font-heading text-foreground leading-snug">
                  {product.name}
                </h2>
                {product.shortDescription && (
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                    {product.shortDescription}
                  </p>
                )}
              </div>

              {/* Product Specifications Grid */}
              <div className="grid grid-cols-2 gap-2.5 pt-3 border-t text-xs">
                <div className="p-2.5 rounded-lg border bg-primary/5 border-primary/20">
                  <span className="text-muted-foreground">{rolePriceLabel}</span>
                  <p className="font-bold text-base text-primary">{formatInr(unitPrice)}</p>
                </div>
                <div className="p-2.5 rounded-lg border bg-card">
                  <span className="text-muted-foreground">Customer Price (CP)</span>
                  <p className="font-bold text-sm text-foreground">{formatInr(product?.customerPrice || product?.mrp || unitPrice)}</p>
                </div>
                <div className="p-2.5 rounded-lg border bg-card">
                  <span className="text-muted-foreground">Max Retail Price (MRP)</span>
                  <p className="font-bold text-sm text-foreground">{formatInr(product?.mrp || unitPrice)}</p>
                </div>
                <div className="p-2.5 rounded-lg border bg-card">
                  <span className="text-muted-foreground">Business Volume (BV)</span>
                  <p className="font-bold text-sm text-amber-600 dark:text-amber-400">
                    {product?.businessVolume ?? 0} BV / unit
                  </p>
                </div>
                <div className="p-2.5 rounded-lg border bg-card col-span-2">
                  <span className="text-muted-foreground">Packaging / Unit</span>
                  <p className="font-bold text-sm text-foreground">
                    {product?.unitValue ? `${product.unitValue} ` : ""}{product?.unit || "piece"}
                  </p>
                </div>
              </div>

              {/* Available Stock Banner */}
              <div
                className={`p-3 rounded-xl border flex items-center justify-between text-xs font-medium ${
                  isOutOfStock
                    ? "bg-destructive/10 border-destructive/20 text-destructive"
                    : "bg-success/10 border-success/20 text-success"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Boxes className="h-4 w-4" />
                  <span>Available with {desk.availableWith}:</span>
                </div>
                <span className="font-bold text-sm tabular-nums">
                  {isOutOfStock ? "0 (Out of Stock)" : `${availableStock} units`}
                </span>
              </div>

              {/* Commission Perk Callout */}
              {onwardCommissionPercent > 0 && (
                <div className="p-3 rounded-xl border bg-primary/5 border-primary/20 text-xs text-primary flex items-start gap-2.5">
                  <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-bold">Commission Benefit: </span>
                    You earn <span className="font-bold underline">{onwardCommissionPercent}%</span> of CP when {desk.earnWhen}.
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Order Configuration & Dynamic Totals (7 Cols) */}
        <div className="lg:col-span-7 space-y-5">
          <Card className="border border-border/80 shadow-sm">
            <CardHeader className="pb-4 border-b">
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-primary" />
                Configure Stock Order
              </CardTitle>
              <CardDescription className="text-xs">
                Select your desired quantity and payment method. The order will be submitted to {desk.availableWith}.
              </CardDescription>
            </CardHeader>

            <form onSubmit={handleSubmit}>
              <CardContent className="p-5 space-y-5">
                {/* Out of Stock Notice */}
                {isOutOfStock && (
                  <div className="p-4 rounded-xl border border-destructive/30 bg-destructive/10 text-destructive flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                    <div className="space-y-1 text-sm">
                      <p className="font-bold">Product Currently Out of Stock</p>
                      <p className="text-xs leading-relaxed">
                        {desk.availableWith} has 0 units of this product available in channel inventory.
                        You cannot place an order until stock is replenished.
                      </p>
                    </div>
                  </div>
                )}

                {/* 1. Order Quantity Input */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="order-qty" className="text-sm font-bold text-foreground">
                      Order Quantity (Units) <span className="text-destructive">*</span>
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      Max available: <strong className="text-foreground">{availableStock}</strong>
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={isOutOfStock || quantity <= 1}
                      onClick={() => handleQuantityChange(quantity - 1)}
                      className="h-10 w-10 shrink-0"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>

                    <Input
                      id="order-qty"
                      type="number"
                      min={1}
                      max={availableStock}
                      value={isOutOfStock ? 0 : quantity}
                      disabled={isOutOfStock}
                      onChange={handleInputChange}
                      className="h-10 text-center font-bold text-base bg-background tabular-nums"
                    />

                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={isOutOfStock || quantity >= availableStock}
                      onClick={() => handleQuantityChange(quantity + 1)}
                      className="h-10 w-10 shrink-0"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>

                    {availableStock > 0 && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => handleQuantityChange(availableStock)}
                        className="h-10 text-xs px-3 shrink-0 font-medium"
                      >
                        Max ({availableStock})
                      </Button>
                    )}
                  </div>

                  {qtyError && (
                    <p className="text-xs text-destructive flex items-center gap-1 font-medium">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {qtyError}
                    </p>
                  )}
                </div>

                {/* 2. Payment Method */}
                <div className="space-y-2">
                  <Label htmlFor="payment-method" className="text-sm font-bold text-foreground">
                    Payment Method <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={paymentMethod}
                    onValueChange={setPaymentMethod}
                    disabled={isOutOfStock}
                  >
                    <SelectTrigger id="payment-method" className="h-11 bg-background text-sm">
                      <SelectValue placeholder="Select Payment Method" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethods.map((pm) => (
                        <SelectItem key={pm.value} value={pm.value}>
                          <div className="flex flex-col text-left py-0.5">
                            <span className="font-semibold text-sm">{pm.label}</span>
                            <span className="text-xs text-muted-foreground">{pm.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* 3. Payment Reference / Note */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="payment-ref" className="text-xs font-semibold text-foreground">
                      Payment Reference (Optional)
                    </Label>
                    <Input
                      id="payment-ref"
                      placeholder="e.g. UTR / Cheque / Txn ID"
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      disabled={isOutOfStock}
                      className="h-9 bg-background text-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="order-note" className="text-xs font-semibold text-foreground">
                      Order Note (Optional)
                    </Label>
                    <Input
                      id="order-note"
                      placeholder="e.g. Urgent dispatch needed"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      disabled={isOutOfStock}
                      className="h-9 bg-background text-sm"
                    />
                  </div>
                </div>

                {/* 4. Live Dynamic Summary Card */}
                <div className="rounded-xl border border-border/80 bg-muted/30 p-4 space-y-3">
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    Order Price & Volume Breakdown
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Unit Purchase Price ({rolePriceLabel})</span>
                      <span className="font-bold text-foreground">{formatInr(unitPrice)}</span>
                    </div>
                    {product.customerPrice && product.customerPrice > unitPrice && (
                      <div className="flex items-center justify-between text-xs text-emerald-600 dark:text-emerald-400">
                        <span>Unit Margin (vs CP {formatInr(product.customerPrice)})</span>
                        <span className="font-semibold">+{formatInr(product.customerPrice - unitPrice)}/unit</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Order Quantity</span>
                      <span className="font-medium text-foreground">{quantity} units</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Total Business Volume (BV)</span>
                      <span className="font-semibold text-amber-600 dark:text-amber-400">
                        {totalBv.toFixed(1)} BV
                      </span>
                    </div>
                    {onwardCommissionPercent > 0 && (
                      <div className="flex items-center justify-between text-xs text-primary">
                        <span>Projected Commission ({onwardCommissionPercent}%)</span>
                        <span className="font-bold">{formatInr(estimatedCommission)}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t flex items-center justify-between">
                      <span className="text-base font-bold text-foreground">Total Order Amount</span>
                      <span className="text-xl font-extrabold text-foreground tracking-tight">
                        {formatInr(totalAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="p-5 border-t bg-muted/10 flex flex-col sm:flex-row items-center justify-between gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(catalogueUrl)}
                  className="w-full sm:w-auto text-sm"
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  size="lg"
                  disabled={
                    isOutOfStock ||
                    quantity < 1 ||
                    quantity > availableStock ||
                    placeOrderMutation.isPending
                  }
                  className="w-full sm:w-auto gap-2 px-8 font-bold shadow-md"
                >
                  <ShoppingCart className="h-4 w-4" />
                  {placeOrderMutation.isPending
                    ? "Submitting Order..."
                    : isOutOfStock
                    ? "Product Out of Stock"
                    : `Confirm & Place Order (${formatInr(totalAmount)})`}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
